'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import toast from 'react-hot-toast';
import logo from '../../assets/logo.svg';
import { newsletterApi } from '../../lib/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faInstagram, faFacebook, faGithub } from '@fortawesome/free-brands-svg-icons';

const FOOTER_LINKS = {
  Product: [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Changelog', href: '#' },
    { label: 'Roadmap', href: '#' },
    { label: 'Status', href: '#' },
  ],
  Company: [
    { label: 'About', href: '#about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Careers', href: '#' },
    { label: 'Contact', href: 'mailto:contact@apporb.net' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Cookie Policy', href: '/privacy#cookies' },
    { label: 'Refund Policy', href: '/terms#refunds' },
  ],
};

const SOCIAL_LINKS = [
  { label: 'YouTube',   href: 'https://www.youtube.com/@caesiumstudio', icon: <FontAwesomeIcon icon={faYoutube}   className="w-5 h-5" /> },
  { label: 'Instagram', href: 'https://www.instagram.com/caesiumstudio', icon: <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" /> },
  { label: 'Facebook',  href: 'https://www.facebook.com/caesiumstudio/', icon: <FontAwesomeIcon icon={faFacebook}  className="w-5 h-5" /> },
  { label: 'GitHub',    href: 'https://github.com/apporbai/apporb-feedback/issues', icon: <FontAwesomeIcon icon={faGithub}    className="w-5 h-5" /> },
];

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await newsletterApi.subscribe(email);
      toast.success("You're subscribed! Check your inbox.");
      setEmail('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="flex w-full md:w-auto gap-2" onSubmit={handleSubmit}>
      <input
        type="email"
        required
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={loading}
        className="flex-1 md:w-64 px-4 py-2.5 rounded-xl text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-60 placeholder:text-white/50"
      />
      <button
        type="submit"
        disabled={loading}
        className="px-5 py-2.5 bg-white/20 hover:bg-white/30 border border-white/30 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap disabled:opacity-60"
      >
        {loading ? 'Subscribing…' : 'Subscribe'}
      </button>
    </form>
  );
}

export function LandingFooter() {
  return (
    <footer
      id="about"
      className="border-t border-[#e2e8f0]"
      style={{ background: '#f8faff' }}
    >
      {/* Newsletter bar */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)' }} className="py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-bold text-white">Stay in the loop</h3>
            <p className="text-blue-200 text-sm mt-1">
              Product updates, ASO tips, and early access to new features.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="col-span-2 space-y-4">
            <Link href="/home" className="flex items-center gap-2.5">
              <Image src={logo} alt="Apporb" width={32} height={32} className="rounded-xl" />
              <span className="font-bold text-[#0f172a] text-base">Apporb</span>
            </Link>
            <p className="text-sm text-[#475569] leading-relaxed max-w-xs">
              The all-in-one workspace for indie mobile developers who want App Store traction — without hiring an ASO agency.
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-[#64748b] hover:text-[#1d4ed8] hover:bg-[#eff6ff] transition-colors border border-[#e2e8f0]"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(FOOTER_LINKS).map(([group, links]) => (
            <div key={group} className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[#0f172a]">{group}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm text-[#64748b] hover:text-[#1d4ed8] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-[#e2e8f0] flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#94a3b8]">
          <p>© {new Date().getFullYear()} Coding Deep Dive inh. Ravi Kumar Sharma. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-[#1d4ed8] transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-[#1d4ed8] transition-colors">Terms of Service</a>
            <a href="mailto:contact@apporb.net" className="hover:text-[#1d4ed8] transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
